<template>
    <section class="bond-metrics-grid">
        <div class="metrics-grid" role="list">
            <div v-for="metric in metricItems" :key="metric.label" class="metric-card" role="listitem">
                <div class="metric-card__icon">
                    <img :src="metric.icon" alt="" aria-hidden="true" />
                </div>
                <div class="metric-card__content">
                    <div class="metric-card__value" :class="{ 'metric-card__value--green': metric.label === 'YTM' }">
                        {{ metric.value }}
                    </div>
                    <div class="metric-card__label">
                        {{ metric.label }}
                        <VTooltip
                            v-if="tooltips[metric.label]"
                            :text="tooltips[metric.label] ?? metric.label"
                            location="top"
                            class="bond-metrics-tooltip"
                        >
                            <template #activator="{ props }">
                                <button
                                    v-bind="props"
                                    type="button"
                                    class="info-icon-wrapper"
                                    :aria-label="`More information about ${metric.label}: ${tooltips[metric.label] ?? metric.label}`"
                                >
                                    <VIcon class="metric-card__info-icon" size="14" aria-hidden="true">
                                        mdi-information-outline
                                    </VIcon>
                                </button>
                            </template>
                        </VTooltip>
                    </div>
                </div>
        </div>
        </div>
    </section>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import metricYtmIcon from '@/assets/icons/bond-details-v2/metric-ytm-trending-up.svg'
import metricMinimumInvestmentIcon from '@/assets/icons/bond-details-v2/metric-minimum-investment-rupee.svg'
import metricPayoutIcon from '@/assets/icons/bond-details-v2/metric-payout-calendar.svg'
import metricMaturityIcon from '@/assets/icons/bond-details-v2/metric-maturity-calendar-clock.svg'
import metricCouponIcon from '@/assets/icons/bond-details-v2/metric-coupon-percent.svg'
import metricFaceValueIcon from '@/assets/icons/bond-details-v2/metric-face-value-rupee.svg'
import { useBondV2Data } from './bondV2Context'

const data = useBondV2Data()

const metricIcons: Record<string, string> = {
    YTM: metricYtmIcon,
    'Minimum investment': metricMinimumInvestmentIcon,
    Payout: metricPayoutIcon,
    Maturity: metricMaturityIcon,
    'Coupon Rate': metricCouponIcon,
    'Face Value': metricFaceValueIcon
}

const metricItems = computed(() => data.value.metrics.map((metric) => ({
    ...metric,
    icon: metricIcons[metric.label] ?? metricYtmIcon
})))

const tooltips: Record<string, string> = {
    YTM: 'YTM shows the total annual return (%) you will earn if you hold this bond till maturity, considering the current price, coupon payments, and face value. It is calculated using the XIRR method.',
    'Minimum investment': 'Minimum Investment shows the investment amount for one bond unit, including price and accrued interest where applicable.',
    Payout: 'Payout indicates how often you will receive interest from this bond — such as Monthly, Quarterly, Half-Yearly, or Yearly.',
    Maturity: 'Maturity Date is the date on which the bond issuer will repay the face value to the investor, along with the final interest payout.',
    'Coupon Rate': "Coupon is the fixed annual interest rate (%) paid by the issuer on the bond's face value. It determines your regular interest income.",
    'Face Value': 'Face Value is the original value of one bond unit on which interest is calculated and which is repaid at maturity.'
}
</script>

<style scoped>
.bond-metrics-grid {
    flex: 1;
    display: block;
    width: 100%;
    min-width: 0;
    background: #F9FBFF;
    border-radius: 0;
    padding: 0;
    box-sizing: border-box;
}

.metrics-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
    width: 100%;
}

.metric-card {
    background: #fff;
    border-radius: 7px;
    padding: 16px 12px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    min-height: 116px;
}

.metric-card__icon {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: #eaf7ff;
    color: #0027af;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.metric-card__icon img {
    width: 20px;
    height: 20px;
    display: block;
}

.metric-card__content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    width: 100%;
}

.metric-card__value {
    font-family: var(--font-family-primary);
    font-size: 17px;
    font-weight: 700;
    color: #1b2559;
    line-height: 17px;
    text-align: center;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
}

.metric-card__value--green {
    color: #15803d;
}

.metric-card__label {
    color: #5b6684;
    text-align: center;
    font-family: Inter, var(--font-family-primary);
    font-size: 11px;
    font-style: normal;
    font-weight: 500;
    line-height: 14px;
    letter-spacing: 0;
    display: flex;
    align-items: center;
    gap: 4px;
}

.info-icon-wrapper {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    border: none;
    border-radius: 50%;
    background: transparent;
    cursor: pointer;
    padding: 0;
}

.metric-card__info-icon {
    color: #0027af;
    font-size: 14px;
}

@media (max-width: 700px) {
    .metrics-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 8px;
    }

    .metric-card {
        min-height: 98px;
        padding: 12px 8px;
        gap: 8px;
    }

    .metric-card__icon {
        width: 28px;
        height: 28px;
    }

    .metric-card__icon img {
        width: 18px;
        height: 18px;
    }

    .metric-card__value {
        font-size: 15px;
        line-height: 16px;
    }

    .metric-card__label {
        font-size: 11px;
        line-height: 14px;
    }
}
</style>

<style>
.bond-metrics-tooltip .v-overlay__content,
.bond-metrics-tooltip.v-tooltip .v-overlay__content {
    min-width: 180px !important;
    max-width: 280px !important;
    white-space: normal !important;
    overflow-wrap: break-word !important;
    text-align: left !important;
    font-size: 12px !important;
}
</style>
